import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Plus, Smartphone } from 'lucide-react';
import { DataTable, type Column } from '../components/data-table';
import { PageHeader, Row, Section, Select } from '../components/form';
import { Alert, Badge, Button, Card, Input } from '../components/ui';
import { ApiError, api, type Extension } from '../lib/api';

interface Device {
  id: string;
  mac: string;
  vendor: string;
  model: string | null;
  label: string | null;
  extensionId: string;
  lastSeenAt: string | null;
  firmware: string | null;
}

interface Provisioned {
  id: string;
  mac: string;
  provisioningUrl: string;
}

const VENDORS = [
  { value: 'yealink', label: 'Yealink' },
  { value: 'grandstream', label: 'Grandstream' },
  { value: 'fanvil', label: 'Fanvil' },
  { value: 'generic', label: 'Other / generic' },
] as const;

/**
 * Desk-phone auto-provisioning.
 *
 * A phone is matched by MAC and fetches its own configuration, so nobody types a SIP password
 * into a handset. The provisioning URL carries a token and is shown once: anyone holding it can
 * read that phone's credentials.
 */
export function Devices() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [provisioned, setProvisioned] = useState<Provisioned | null>(null);

  const { data: devices = [] } = useQuery({
    queryKey: ['devices'],
    queryFn: () => api.get<Device[]>('/api/v1/devices'),
  });

  const { data: extensions = [] } = useQuery({
    queryKey: ['extensions'],
    queryFn: () => api.get<Extension[]>('/api/v1/extensions'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/devices/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['devices'] }),
  });

  const numberFor = (id: string) => extensions.find((e) => e.id === id)?.number ?? '—';

  const columns: Array<Column<Device>> = [
    {
      key: 'mac',
      header: 'MAC address',
      sort: (a, b) => a.mac.localeCompare(b.mac),
      render: (d) => <code className="text-xs">{d.mac}</code>,
    },
    {
      key: 'vendor',
      header: 'Phone',
      sort: (a, b) => a.vendor.localeCompare(b.vendor),
      render: (d) => (
        <span>
          <Badge>{d.vendor}</Badge>
          {d.model && <span className="ml-2 text-muted-foreground">{d.model}</span>}
        </span>
      ),
    },
    {
      key: 'extension',
      header: 'Extension',
      render: (d) => <span className="font-mono">{numberFor(d.extensionId)}</span>,
    },
    { key: 'label', header: 'Label', render: (d) => d.label ?? '—' },
    {
      key: 'lastSeen',
      header: 'Last provisioned',
      sort: (a, b) => (a.lastSeenAt ?? '').localeCompare(b.lastSeenAt ?? ''),
      render: (d) =>
        d.lastSeenAt ? (
          new Date(d.lastSeenAt).toLocaleString()
        ) : (
          <span className="text-muted-foreground">Never</span>
        ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Auto provisioning"
        description="Desk phones configure themselves from their MAC address."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus size={14} />
            Add phone
          </Button>
        }
      />

      {provisioned && (
        <ProvisionedCard provisioned={provisioned} onDismiss={() => setProvisioned(null)} />
      )}

      {creating && (
        <DeviceForm
          extensions={extensions}
          onCancel={() => setCreating(false)}
          onCreated={(result) => {
            setCreating(false);
            setProvisioned(result);
            void queryClient.invalidateQueries({ queryKey: ['devices'] });
          }}
        />
      )}

      <DataTable
        rows={devices}
        columns={columns}
        rowKey={(d) => d.id}
        search={(d) => `${d.mac} ${d.vendor} ${d.model ?? ''} ${d.label ?? ''}`}
        searchPlaceholder="Search phones"
        onDelete={(rows) => rows.forEach((r) => remove.mutate(r.id))}
        deleteLabel={(rows) =>
          `Remove ${rows.length} phone(s)? They keep working until rebooted, then cannot re-provision.`
        }
        empty="No phones registered for provisioning."
      />
    </div>
  );
}

function ProvisionedCard({
  provisioned,
  onDismiss,
}: {
  provisioned: Provisioned;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <Card className="space-y-4 border-warning/40 bg-warning/5">
      <div className="flex items-center gap-2">
        <Smartphone size={16} className="text-warning" />
        <h2 className="font-medium">Phone {provisioned.mac} added</h2>
      </div>

      <Alert tone="warning">
        Anyone with this URL can read the phone's SIP password, so it is shown once and not
        retrievable from the list.
      </Alert>

      <div className="flex items-center gap-2 rounded-[--radius] border border-border bg-card p-3">
        <code className="min-w-0 flex-1 truncate text-xs">{provisioned.provisioningUrl}</code>
        <button
          onClick={() => {
            void navigator.clipboard.writeText(provisioned.provisioningUrl).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
          className="shrink-0 rounded-[--radius] p-1.5 text-muted-foreground hover:bg-muted"
          aria-label="Copy provisioning URL"
        >
          {copied ? <Check size={14} className="text-success" /> : <Copy size={14} />}
        </button>
      </div>

      <p className="text-sm text-muted-foreground">
        Set this as the phone's provisioning server, then reboot it. On a network you control, DHCP
        option 66 pointing here does the same thing for every phone at once.
      </p>

      <Button variant="secondary" onClick={onDismiss}>
        Done
      </Button>
    </Card>
  );
}

function DeviceForm({
  extensions,
  onCancel,
  onCreated,
}: {
  extensions: Extension[];
  onCancel: () => void;
  onCreated: (result: Provisioned) => void;
}) {
  const [mac, setMac] = useState('');
  const [vendor, setVendor] = useState<string>('yealink');
  const [model, setModel] = useState('');
  const [label, setLabel] = useState('');
  const [extensionId, setExtensionId] = useState(extensions[0]?.id ?? '');

  const create = useMutation({
    mutationFn: () =>
      api.post<Provisioned>('/api/v1/devices', {
        mac,
        vendor,
        extensionId,
        ...(model ? { model } : {}),
        ...(label ? { label } : {}),
      }),
    onSuccess: onCreated,
  });

  const conflict = create.error instanceof ApiError && create.error.isConflict;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <Section
        title="Add a phone"
        description="The MAC address is on a label underneath the phone."
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <Row
            label="MAC address"
            required
            hint="Any format: colons, dashes or none."
            error={conflict ? 'That MAC is already registered' : undefined}
          >
            <Input
              value={mac}
              onChange={(e) => setMac(e.target.value)}
              placeholder="00:15:65:ab:cd:ef"
              required
            />
          </Row>

          <Row label="Vendor" required>
            <Select value={vendor} onChange={setVendor}>
              {VENDORS.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </Select>
          </Row>

          <Row label="Model" hint="Optional, for your own reference.">
            <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder="T33G" />
          </Row>

          <Row label="Extension" required>
            <Select value={extensionId} onChange={setExtensionId}>
              {extensions.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.number} — {e.name}
                </option>
              ))}
            </Select>
          </Row>

          <Row label="Label" hint="Where the phone lives.">
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Reception"
            />
          </Row>
        </div>

        {create.error && !conflict && (
          <div className="mt-4">
            <Alert>{(create.error as Error).message}</Alert>
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <Button type="submit" disabled={create.isPending || !mac || !extensionId}>
            {create.isPending ? 'Adding…' : 'Add phone'}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </Section>
    </form>
  );
}
